import { all, fork, put, takeEvery, takeLatest, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

// helpers
import {
  getOrders as getOrderList,
  createOrderApi,
  getOrder as getOrderApi,
  updateOrderApi,
  deleteOrderApi,
  updateStatusOrderApi,
  downloadSampleOrderApi,
  uploadOrdersApi,
  bulkUpdateAgentApi,
} from "../../helpers/";

// constants
import { OrdersActionTypes } from "./constants";
import {
  createOrderError,
  createOrderSuccess,
  ordersGetListError,
  ordersGetListSuccess,
  getOrderSuccess,
  getOrderError,
  updateOrderSuccess,
  updateOrderError,
  deleteOrderSuccess,
  deleteOrderError,
  updateStatusOrderSuccess,
  updateStatusOrderError,
  orderSampleDownloadSuccess,
  orderSampleDownloadError,
  uploadOrdersSuccess,
  uploadOrdersError,
  bulkUpdateAgentSuccess,
  bulkUpdateAgentError,
} from "./actions";

interface OrderData {
  payload: {
    limit: number;
    skip: number;
    keyword?: string;
    owner?: string;
    country?: string;
    status?: string;
    agent?: string;
    product?: string;
    boutique?: string;
  };
  type: string;
}

interface BulkUpdateAgentData {
  payload: {
    orderIds: string[];
    agentId: string;
  };
  type: string;
}

interface OrderCreateData {
  payload: {
    country: string;
    product: string;
    customerName: string;
    customerPhone: string;
    customerAddress: string;
    price: number;
    quantity: number;
    sourceLink: string;
    sourceName: string;
    totalPrice: number;
  };
  type: string;
}

interface OrderUpdateData {
  payload: {
    id: string;
    product: string;
    customerName: string;
    customerPhone: string;
    customerAddress: string;
    price: number;
    quantity: number;
    sourceLink: string;
    sourceName: string;
    totalPrice: number;
  };
  type: string;
}

interface OrderStatusUpdateData {
  payload: {
    id: string;
    status: string;
    comment: string;
    delivery?: string;
    quantity?: number;
    totalPrice?: number;
    affiliateAddOns?: { affiliateId: string; quantity: number; unitPrice?: number }[];
    disposition?: string;
    callbackAt?: string;
    scheduledFor?: string;
  };
  type: string;
}

interface OrderDeleteData {
  payload: {
    ids: string[];
  };
  type: string;
}

interface GetOrderData {
  payload: {
    id: string;
  };
  type: string;
}

// Interface for Upload Data
interface OrderUploadData {
  payload: {
    file: File;
  };
  type: string;
}

function* getOrders({
  payload: { limit, skip, keyword, owner, country, status, agent, product, boutique },
}: OrderData): SagaIterator {
  try {
    const response = yield call(getOrderList, {
      limit,
      skip,
      keyword,
      owner,
      country,
      status,
      agent,
      product,
      boutique,
    });
    // Le response interceptor axios "avale" les 403 en renvoyant
    // {data:null,__handled403:true}. Ne rien dispatcher dans ce cas : on
    // laisse l'état courant intact pour éviter d'effacer les données à
    // l'écran pendant un refresh de token ou un blip de permission.
    if ((response as any)?.__handled403) return;
    const Orders = response.data;
    if (Orders == null) return;
    yield put(ordersGetListSuccess(OrdersActionTypes.GET_LIST_ORDERS, Orders));
  } catch (error: any) {
    yield put(ordersGetListError(OrdersActionTypes.GET_LIST_ORDERS, error));
  }
}

function* createOrder({
  payload: {
    country,
    product,
    customerName,
    customerPhone,
    customerAddress,
    price,
    quantity,
    sourceLink,
    sourceName,
    totalPrice,
  },
}: OrderCreateData): SagaIterator {
  try {
    const response = yield call(createOrderApi, {
      country,
      product,
      customerName,
      customerPhone,
      customerAddress,
      price,
      quantity,
      sourceLink,
      sourceName,
      totalPrice,
    });
    const Order = response.data;
    yield put(createOrderSuccess(OrdersActionTypes.CREATE_ORDER, Order));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(createOrderError(OrdersActionTypes.CREATE_ORDER, errorMessage));
  }
}

function* updateOrder({
  payload: {
    id,
    product,
    customerName,
    customerPhone,
    customerAddress,
    price,
    quantity,
    sourceLink,
    sourceName,
    totalPrice,
  },
}: OrderUpdateData): SagaIterator {
  try {
    const response = yield call(updateOrderApi, {
      id,
      product,
      customerName,
      customerPhone,
      customerAddress,
      price,
      quantity,
      sourceLink,
      sourceName,
      totalPrice,
    });
    const Order = response.data;
    yield put(updateOrderSuccess(OrdersActionTypes.UPDATE_ORDER, Order));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(updateOrderError(OrdersActionTypes.UPDATE_ORDER, errorMessage));
  }
}

function* updateStatusOrder({
  payload: { id, status, comment, delivery, quantity, totalPrice, affiliateAddOns, disposition, callbackAt, scheduledFor },
}: OrderStatusUpdateData): SagaIterator {
  try {
    const response = yield call(updateStatusOrderApi, {
      id,
      status,
      comment,
      delivery,
      quantity,
      totalPrice,
      affiliateAddOns,
      disposition,
      callbackAt,
      scheduledFor,
    });
    const Order = response.data;
    yield put(
      updateStatusOrderSuccess(OrdersActionTypes.UPDATE_STATUS_ORDER, Order)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      updateStatusOrderError(
        OrdersActionTypes.UPDATE_STATUS_ORDER,
        errorMessage
      )
    );
  }
}

function* deleteOrder({ payload: { ids } }: OrderDeleteData): SagaIterator {
  try {
    const response = yield call(deleteOrderApi, {
      ids,
    });
    const Order = response.data;
    yield put(deleteOrderSuccess(OrdersActionTypes.DELETE_ORDER, Order));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(deleteOrderError(OrdersActionTypes.DELETE_ORDER, errorMessage));
  }
}

function* getOrder({ payload: { id } }: GetOrderData): SagaIterator {
  try {
    const response = yield call(getOrderApi, { id });
    const order = response.data;
    yield put(getOrderSuccess(OrdersActionTypes.GET_ORDER, order));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(getOrderError(OrdersActionTypes.GET_ORDER, errorMessage));
  }
}

function* downloadSample(): SagaIterator {
  try {
    const response = yield call(downloadSampleOrderApi, {});
    const users = response.data;
    yield put(
      orderSampleDownloadSuccess(OrdersActionTypes.DOWNLOAD_SAMPLE, users)
    );
  } catch (error: any) {
    yield put(
      orderSampleDownloadError(OrdersActionTypes.DOWNLOAD_SAMPLE, error)
    );
  }
}

function* uploadMultipleOrders({
  payload: { file },
}: OrderUploadData): SagaIterator {
  try {
    const response = yield call(uploadOrdersApi, { file });
    const uploadedData = response.data;
    yield put(
      uploadOrdersSuccess(
        OrdersActionTypes.UPLOAD_MULTIPLE_ORDERS,
        uploadedData
      )
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      uploadOrdersError(OrdersActionTypes.UPLOAD_MULTIPLE_ORDERS, errorMessage)
    );
  }
}

function* bulkUpdateAgent({
  payload: { orderIds, agentId },
}: BulkUpdateAgentData): SagaIterator {
  try {
    const response = yield call(bulkUpdateAgentApi, { orderIds, agentId });
    const result = response.data;
    yield put(
      bulkUpdateAgentSuccess(OrdersActionTypes.BULK_UPDATE_AGENT, result)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      bulkUpdateAgentError(OrdersActionTypes.BULK_UPDATE_AGENT, errorMessage)
    );
  }
}

export function* watchBulkUpdateAgent() {
  yield takeEvery(OrdersActionTypes.BULK_UPDATE_AGENT, bulkUpdateAgent);
}

export function* watchGetOrders() {
  // takeLatest annule automatiquement le saga en cours si une nouvelle
  // action GET_LIST_ORDERS arrive → plus de race condition sur changement
  // rapide de filtre (pays, statut, recherche, tri).
  yield takeLatest(OrdersActionTypes.GET_LIST_ORDERS, getOrders);
}

export function* watchCreateOrders() {
  yield takeEvery(OrdersActionTypes.CREATE_ORDER, createOrder);
}

export function* watchUpdateOrders() {
  yield takeEvery(OrdersActionTypes.UPDATE_ORDER, updateOrder);
}

export function* watchUpdateStatusOrders() {
  yield takeEvery(OrdersActionTypes.UPDATE_STATUS_ORDER, updateStatusOrder);
}

export function* watchDeleteOrders() {
  yield takeEvery(OrdersActionTypes.DELETE_ORDER, deleteOrder);
}

export function* watchGetOrder() {
  yield takeEvery(OrdersActionTypes.GET_ORDER, getOrder);
}

export function* watchDownloadSampleOrder() {
  yield takeEvery(OrdersActionTypes.DOWNLOAD_SAMPLE, downloadSample);
}

// Watcher for uploadMultipleOrders
export function* watchUploadOrders() {
  yield takeEvery(
    OrdersActionTypes.UPLOAD_MULTIPLE_ORDERS,
    uploadMultipleOrders
  );
}

function* ordersSaga() {
  yield all([
    fork(watchGetOrders),
    fork(watchCreateOrders),
    fork(watchGetOrder),
    fork(watchUpdateOrders),
    fork(watchDeleteOrders),
    fork(watchUpdateStatusOrders),
    fork(watchDownloadSampleOrder),
    fork(watchUploadOrders),
    fork(watchBulkUpdateAgent),
  ]);
}

export default ordersSaga;
